import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../../../core/api/queries';
import { useReportPage } from '../../../shared/hooks';
import { ReportPageLayout } from '../../../shared/components/layouts';
import { columnBuilder } from '../../../shared/utils';
import { renderSchoolName } from '../../../shared/components/tables';
import { useAuthProviderV2, useRoutesProvider } from '../../../providers/provider-hooks';
import type { SchoolReportDto } from '../api/schools.api';

/**
 * Schools Report Page
 *
 * Displays all registered schools with filtering, sorting, and export capabilities.
 *
 * Features:
 * - 9 columns: name, CUI, SIIIR code, SE code, county, city, professor, students, phone
 * - Server-side filtering on all text/number fields
 * - Server-side sorting
 * - Excel export
 * - Responsive layout
 *
 * Architecture (Refactored 2025-11-01):
 * - Uses useReportPage hook (consolidates useState, useQuery, useSpringReport, useReportExport)
 * - Uses ReportPageLayout component (consolidates ReportControls + DataTable + SpringPagination JSX)
 * - Uses columnBuilder for column definitions
 * - Reduced from 120 lines to 50 lines (58% reduction)
 */
export function SchoolsReportPage() {
    // Titlul se citeste din intrarea de meniu care se potriveste cu ruta curenta. Pana pe
    // 2026-09-21 pagina era inregistrata si pe o ruta PROFESOR ("Scoli conform Minister
    // Educatie"), scoasa prin decizia owner-ului; azi ramane doar "Raport Scoli" (ADMIN/DBEST).
    const { pathname } = useLocation();
    const { NAV_LINKS_V2, ROLES } = useRoutesProvider();
    const { userInfo } = useAuthProviderV2();
    // Telefonul profesorului e contact: backendul il goleste pentru orice rol din afara DBEST/ADMIN
    // (SchoolContactStripper) si ignora filtrul pe el. Ruta e azi doar ADMIN/DBEST; garda ramane
    // ca sa nu se deschida tacut coloana daca pagina primeste vreodata alt rol.
    const vedeContact = userInfo?.role === ROLES.ADMIN || userInfo?.role === ROLES.DBEST;
    const titlu = Object.values(NAV_LINKS_V2).find((link) => link.PATH === pathname)?.TEXT ?? 'Raport Școli';

    // ==========================================
    // 1. DATA FETCHING & REPORT INTERFACE
    // ==========================================

    const { report, adapters, handleExport } = useReportPage({
        queryHook: api.schools.report,
        initialSort: ['numeInstitutieInvatamant,asc'],
        exportEndpoint: '/api/schools/report',
        exportFilename: 'raport_scoli'
    });

    // ==========================================
    // 2. COLUMN DEFINITIONS
    // ==========================================

    const columns = useMemo(() =>
        columnBuilder<SchoolReportDto>()
            // `render` normalizeaza numele DOAR la afisare; filtrul si exportul raman
            // pe textul original. `cardRole` il face titlul randului in rezumatul de pe mobil.
            .text('numeInstitutieInvatamant', 'Nume Instituție', 'Denumire instituție...', {
                render: renderSchoolName,
                cardRole: 'title',
            })
            .text('cui', 'CUI', 'C.U.I....')
            .number('codSiiirUnitate', 'Cod SIIIR', 'Cod SIIIR...')
            .text('codSe', 'Cod SE', 'Cod SE...', { sortable: false })
            .nested('adresa.judet', 'Județ', (row) => row.adresa?.judet, { filterPlaceholder: 'Județ...' })
            .nested('adresa.oras', 'Oraș', (row) => row.adresa?.oras, { filterPlaceholder: 'Oraș...' })
            .nested('professor.nume', 'Profesor', (row) => row.professor?.nume, { filterPlaceholder: 'Nume profesor...' })
            .when(vedeContact, (b) => b
                .nested('professor.telefon', 'Telefon profesor', (row) => row.professor?.telefon, { filterPlaceholder: 'Telefon profesor...' }))
            // Cifra de langa numele scolii in rezumatul de pe mobil.
            .number('numarElevi', 'Nr. Elevi', 'Număr elevi...', { cardRole: 'value' })
            .text('telefon', 'Telefon', 'Telefon...')
            .build(),
        [vedeContact]
    );

    // ==========================================
    // 3. RENDER
    // ==========================================

    return (
        <ReportPageLayout
            title={titlu}
            report={report}
            adapters={adapters}
            handleExport={handleExport}
            columns={columns}
            entityLabel="scoli"
            unitLabel="elevi"
        />
    );
}
